import _ from 'lodash'
import React from 'react'
import queryString from 'query-string'
import apis from 'browser/app/models/apis'
import { Try } from 'shared-libs/helpers/utils'
import { Entity } from 'shared-libs/models/entity'
import { SafeView } from 'shared-libs/components/view/safe-view'
import MobileComponentsMap from 'browser/mobile/components/index'
import { unpackToolsURL } from 'browser/app/pages/app/tools/utils'
import { EntityRenderer } from 'shared-libs/components/entity/renderer'
import { Button } from 'browser/components/atomic-elements/atoms/button/button'
import { SandboxError, SandboxSession } from 'shared-libs/helpers/sandbox/sandbox-session'
import { globalTranslationTable } from 'browser/mobile/util/global-translation-table'
import { BarcodeScanner } from 'browser/mobile/components/barcode/barcode-scanner-modal'
import { AppNavigatorContext } from 'browser/contexts/app-navigator/app-navigator-context'

import './_mobile-web-sandbox.scss'

const DEFAULT_PORT = 3000
const SANDBOX_SERVER_STORAGE_KEY = 'sandboxServerUrl'

interface IMobileWebSandboxState {
  isConnected?: boolean
  isScanning?: boolean
  serverUrl?: string
  entity?: Entity
  schema?: Entity
  error?: SandboxError
}

export class MobileWebSandbox extends React.Component<{}, IMobileWebSandboxState> {
  private safeViewRef = React.createRef<SafeView>()
  private session: SandboxSession
  private isDeeplink: boolean

  constructor(props: {}) {
    super(props)

    this.session = new SandboxSession({
      role: 'client',
      apis,
      onConnected: this.handleConnected,
      onDisconnected: this.handleDisconnected,
      onError: this.handleError,
      onEntities: this.handleEntities,
    })

    const deeplinkState = this.getDeeplinkState()
    this.isDeeplink = Boolean(deeplinkState.schema && deeplinkState.entity)
    this.state = {
      serverUrl: this.getInitialServerUrl(),
      ...deeplinkState,
    }
  }

  public componentDidMount() {
    if (this.isDeeplink) {
      return
    }
    this.connect()
  }

  public componentWillUnmount() {
    this.session.close()
  }

  public render() {
    const { schema, entity, isConnected } = this.state

    if (!this.isDeeplink && !isConnected) {
      return this.renderDisconnected()
    }

    if (!schema || !entity) {
      return this.renderWaitingForSchema()
    }

    return this.renderEntity(schema, entity)
  }

  private renderEntity(schema: Entity, entity: Entity) {
    return (
      <AppNavigatorContext.Consumer>
        {({ settings }) => (
          <SafeView
            ref={this.safeViewRef}
            errorInvalidationKey={schema.get('uiSchema')}
            renderError={this.renderError}
          >
            <EntityRenderer
              componentsMap={MobileComponentsMap}
              onChangeComplete={this.handleUpdateEntity}
              state={{ settings }}
              translationTable={globalTranslationTable}
              uiSchemaPath="uiSchema"
              value={entity}
            />
          </SafeView>
        )}
      </AppNavigatorContext.Consumer>
    )
  }

  private renderScanner() {
    return (
      <div className="mobile-web-sandbox__scanner">
        <BarcodeScanner
          onCapture={this.handleScanned}
          onClose={() => this.setState({ isScanning: false })}
          helpText="Scan the QR code shown in the desktop web sandbox host"
        />
      </div>
    )
  }

  private renderWaitingForSchema() {
    return (
      <div className="pa3 pre-wrap">
        Connected to the sandbox server, waiting for a UI schema. Open the desktop web sandbox and
        select the "Mobile Web" tab — that registers it as the host and broadcasts here.
      </div>
    )
  }

  private renderDisconnected() {
    const { error, isScanning } = this.state

    return (
      <div className="pa3">
        {isScanning && this.renderScanner()}
        {error && this.renderError(error)}
        <div className="mb3 pre-wrap">
          Not connected. Make sure the sandbox-server is running, then use an appropriate ws:// URL
          below (or, if using a mobile device, scan the QR shown by the desktop web sandbox host to
          use the sandbox server's local IP) and try connecting again.
        </div>
        <div className="mb1">Sandbox server:</div>
        <input
          className="pa2 mb3 mobile-web-sandbox__server-input"
          value={this.state.serverUrl ?? ''}
          onChange={this.handleServerUrlChange}
          placeholder="ws://<host>:3000"
        />
        <div className="flex flex-column">
          <Button
            className="mb2 mobile-web-sandbox__button"
            size="large"
            intent="primary"
            buttonText="Retry connection"
            onClick={this.handleRetry}
          />
          <Button
            className="mobile-web-sandbox__button"
            size="large"
            buttonText="Scan QR to connect"
            onClick={this.handleScanClicked}
          />
        </div>
      </div>
    )
  }

  private renderError = (error: SandboxError | Error) => {
    return <div className="pa3 mb3 pre-wrap mobile-web-sandbox__error">{error.message}</div>
  }

  private handleRetry = () => {
    this.reconnect()
  }

  private handleScanned = (serverUrl: string) => {
    this.setState({ isScanning: false, serverUrl }, this.reconnect)
  }

  private handleServerUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ serverUrl: e.target.value })
  }

  private handleScanClicked = () => {
    if (!window.isSecureContext) {
      window.alert(
        'QR scanning needs a secure context (HTTPS or localhost). Serve this page over HTTPS, or pass the sandbox server URL directly via the ?serverUrl= query parameter.'
      )
      return
    }

    this.setState({ isScanning: true })
  }

  private reconnect() {
    this.setState({ error: undefined })
    this.session.close()
    this.connect()
  }

  private connect() {
    this.session.connect(this.getSandboxServerUrl())
  }

  private handleConnected = () => {
    console.log('[sandbox] connected to server')
    try {
      localStorage.setItem(SANDBOX_SERVER_STORAGE_KEY, this.getSandboxServerUrl())
    } catch (e) {
      console.warn('[sandbox] failed to persist server url', e)
    }
    this.setState({ isConnected: true, error: undefined })
  }

  private handleEntities = (schema: Entity, entity: Entity) => {
    console.log('[sandbox] received schema + entity')
    this.setState({ schema, entity })
  }

  private handleError = (error: SandboxError) => {
    this.setState({ error })
  }

  private handleDisconnected = () => {
    const defaultError: SandboxError = {
      type: 'connection',
      message: 'Disconnected from sandbox server',
    }
    const { error = defaultError } = this.state
    this.setState({ isConnected: false, error })
  }

  private handleUpdateEntity = (entity: Entity) => {
    this.forceUpdate()
    this.sendEntityContent(entity)
  }

  private sendEntityContent = _.debounce((entity: Entity) => {
    const { schema } = this.state
    this.session.sendEntityContent(entity.content, schema?.content)
  }, 400)

  private getSandboxServerUrl(): string {
    return this.state.serverUrl || this.getInitialServerUrl()
  }

  private getInitialServerUrl(): string {
    const { serverUrl, port } = queryString.parse(location.search)
    const savedUrl = Try(() => localStorage.getItem(SANDBOX_SERVER_STORAGE_KEY))
    return (
      (serverUrl as string) ||
      savedUrl ||
      `ws://localhost:${port || DEFAULT_PORT}`
    )
  }

  private getDeeplinkState(): IMobileWebSandboxState {
    const { schemaString, entityString } = unpackToolsURL() as {
      schemaString?: string
      entityString?: string
    }
    if (schemaString && entityString) {
      return this.session.reconstructEntities(JSON.parse(schemaString), JSON.parse(entityString))
    }
    return {}
  }
}
